/**
 * TrackDropPicker — small modal for artists to pick one of their own tracks
 * and post it into chat as a rich card. Fetches the artist's tracks once when
 * opened; no debouncing needed because the list is small (<=150 per artist).
 */
import { useEffect, useMemo, useState } from "react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Music, Search } from "lucide-react";
import { toast } from "sonner";

export default function TrackDropPicker({ open, onClose, onPick }) {
  const { user } = useAuth();
  const [tracks, setTracks] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open || !user) return;
    setBusy(true);
    api.get(`/tracks?artist_id=${user.id}&limit=200`)
      .then((r) => setTracks(r.data.tracks || []))
      .catch(() => toast.error("Couldn't load your tracks"))
      .finally(() => setBusy(false));
  }, [open, user]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return tracks;
    return tracks.filter((t) => (t.title || "").toLowerCase().includes(s));
  }, [tracks, q]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="track-drop-picker" className="rounded-none bg-[#0A0A0A] border-[#2A2A2A] max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Drop a track into chat</DialogTitle>
        </DialogHeader>
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            data-testid="track-drop-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search your tracks..."
            className="w-full bg-black border border-[#2A2A2A] pl-8 pr-3 py-2 font-mono text-xs focus:outline-none focus:border-[#FF4500]"
          />
        </div>
        <div className="overflow-y-auto flex-1 -mx-6 px-6">
          {busy && <div className="py-8 text-center font-mono text-xs text-gray-500">Loading...</div>}
          {!busy && filtered.length === 0 && (
            <div className="py-8 text-center font-mono text-xs text-gray-500">
              {tracks.length === 0 ? "You haven't uploaded any tracks yet." : "No matches."}
            </div>
          )}
          <ul className="divide-y divide-[#1a1a1a]">
            {filtered.map((t) => (
              <li key={t.id}>
                <button
                  data-testid={`track-drop-pick-${t.id}`}
                  onClick={() => { onPick(t); onClose(); }}
                  className="w-full flex items-center gap-3 p-2 hover:bg-white/5 text-left"
                >
                  <div className="h-10 w-10 shrink-0 bg-neutral-800 overflow-hidden">
                    {t.cover_path ? (
                      <img src={fileUrl(t.cover_path)} alt="" className="h-full w-full object-cover" />
                    ) : (
                      <div className="h-full w-full grid place-items-center text-gray-600"><Music className="h-5 w-5" /></div>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-sm truncate">{t.title}</div>
                    <div className="font-mono text-[10px] text-gray-500 uppercase tracking-widest">
                      {t.is_free ? "FREE" : `$${((t.price_cents || 99) / 100).toFixed(2)}`}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <div className="flex justify-end">
          <Button
            data-testid="track-drop-cancel"
            variant="outline"
            onClick={onClose}
            className="rounded-none border-[#2A2A2A] bg-transparent font-mono text-xs uppercase tracking-widest"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
